import Accordion from '@mui/material/Accordion';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useEffect, ReactNode, ComponentType } from 'react';

import { useRouter } from 'next/router';
import Link from 'next/link';
import * as React from 'react';
import { createContext, useContext, useState } from 'react';

import { SyntheticEvent } from 'react';
import { CustomDrawerContents } from './CustomSidebarContents';
import { typography } from '@/config/theme/typography';

export const drawerWidth = 280;
export const drawerClosedWidth = 88;

const SIDEBAR_STATE_KEY = 'sidebar_state';

interface CustomSidebarContextValue {
  isOpen: boolean;
  toggleSidebar: () => void;
}

const CustomSidebarContext = createContext<
  CustomSidebarContextValue | undefined
>(undefined);

interface CustomSidebarProviderProps {
  children: ReactNode;
}

export const CustomSidebarProvider = ({
  children,
}: CustomSidebarProviderProps) => {
  const [isOpen, setIsOpen] = useState<boolean | undefined>(undefined);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'), {
    noSsr: true,
  });

  useEffect(() => {
    const localStorageSideBarState = localStorage.getItem(SIDEBAR_STATE_KEY);
    if (
      localStorageSideBarState === 'undefined' ||
      localStorageSideBarState === null
    ) {
      const defaultState = isMobile ? 'closed' : 'open';
      localStorage.setItem(SIDEBAR_STATE_KEY, defaultState);
    }
    setIsOpen(localStorageSideBarState !== 'closed');
    if (isMobile) {
      setIsOpen(false);
    }
  }, []);

  const toggleSidebar = (): void => {
    const newState = !isOpen;
    setIsOpen(newState);
    //localStorage é meio bugado quando não armazena string
    localStorage.setItem(SIDEBAR_STATE_KEY, newState ? 'open' : 'closed');
  };

  return (
    <CustomSidebarContext.Provider value={{ isOpen: isOpen!, toggleSidebar }}>
      {children}
    </CustomSidebarContext.Provider>
  );
};

export const useCustomSidebar = (): CustomSidebarContextValue => {
  const context = useContext(CustomSidebarContext);
  if (context === undefined) {
    throw new Error(
      'useCustomSidebar deve estar dentro de um CustomSidebarProvider',
    );
  }
  return context;
};

export function CustomSidebar() {
  const { isOpen, toggleSidebar } = useCustomSidebar();

  const CustomDrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: theme.spacing(1, 2),
    ...theme.mixins.toolbar,
    position: isOpen ? 'relative' : 'relative',
    top: 0,
    right: 0,
    minHeight: 'auto !important',
  }));

  if (isOpen == null) return null;

  return (
    <>
      {/* Desktop */}
      <Drawer
        variant='temporary'
        open={isOpen}
        onClose={toggleSidebar}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            backgroundColor: 'primary.dark',
            color: 'neutral.0',
            boxSizing: 'border-box',
            width: isOpen ? drawerWidth : drawerClosedWidth,
            transition: 'width 0.3s',
            overflowX: 'hidden',
            zIndex: 3,
          },
        }}
      >
        <CustomDrawerContents
          isOpen={isOpen}
          toggleSidebar={toggleSidebar}
          CustomDrawerHeader={
            CustomDrawerHeader as ComponentType<{
              sx?: object;
              children?: React.ReactNode;
            }>
          }
        />
      </Drawer>
      {/* Mobile */}
      <Drawer
        variant='permanent'
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            backgroundColor: 'primary.dark',
            color: 'neutral.0',
            boxSizing: 'border-box',
            width: isOpen ? drawerWidth : drawerClosedWidth,
            transition: 'width 0.3s',
            overflowX: 'hidden',
            position: 'initial',
            height: '100vh',
            zIndex: 3,
          },
        }}
        open
      >
        <CustomDrawerContents
          isOpen={isOpen}
          toggleSidebar={toggleSidebar}
          CustomDrawerHeader={
            CustomDrawerHeader as ComponentType<{
              sx?: object;
              children?: React.ReactNode;
            }>
          }
        />
      </Drawer>
    </>
  );
}

interface CustomSidebarButtonProps {
  name: string;
  Icon: ComponentType<{ sx?: object }>;
  EndIcon?: ComponentType;
  path?: string;
  isIndented?: boolean;
  selectedCallback?: () => void;
  isInsideAccordionSummary?: boolean;
  disabled?: boolean;
}

export function CustomSidebarButton({
  name,
  Icon,
  EndIcon,
  path,
  isIndented,
  selectedCallback,
  isInsideAccordionSummary,
  disabled,
}: CustomSidebarButtonProps) {
  const router = useRouter();
  const buttonSelected =
    path && router.pathname.endsWith(path) && !isInsideAccordionSummary;
  const { isOpen } = useCustomSidebar();

  React.useEffect(() => {
    if (buttonSelected && !!selectedCallback && !isInsideAccordionSummary) {
      selectedCallback();
    }
  }, [
    router.pathname,
    buttonSelected,
    selectedCallback,
    isInsideAccordionSummary,
  ]);

  const bg = buttonSelected ? 'primary.darker' : null;

  const startIcon = (
    <Icon
      sx={{
        marginLeft: '18px',
        marginRight: isOpen ? '18px' : '0',
        width: '24px',
        height: '24px',
      }}
    />
  );
  const endIcon = EndIcon && isOpen ? <EndIcon /> : undefined;

  const commonContainerStyle = {
    width: '100%',
    color: 'neutral.0',
    justifyContent: 'flex-start',
    py: 0,
    height: '56px',
    minWidth: '56px',
    backgroundColor: bg,
    textWrap: 'nowrap' as const,
    transition: 'background-color 0.2s ease-in-out',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'primary.darker',
    },
    '& .MuiSvgIcon-root': {
      mr: 0,
    },
    '& .MuiButton-icon': {
      mr: '12px',
    },
  };

  const animatedNameSpan = (
    <span
      style={{
        opacity: isOpen ? 1 : 0,
        transition: 'opacity 0.3s ease-in-out',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
      }}
    >
      {name}
    </span>
  );

  if (isInsideAccordionSummary) {
    return (
      <Button
        component='div'
        fullWidth
        size='medium'
        variant='text'
        disabled={disabled}
        sx={{
          ...commonContainerStyle,
          pl: '4px',
        }}
        startIcon={startIcon}
        endIcon={endIcon}
      >
        {animatedNameSpan}
      </Button>
    );
  }

  if (path) {
    return (
      <Button
        component={Link}
        href={path}
        fullWidth
        size='medium'
        variant='text'
        disabled={disabled}
        sx={{
          ...commonContainerStyle,
          ...typography['label-large'],
          pl: isIndented ? '56px' : '2px',
          textDecoration: 'none',
        }}
        startIcon={startIcon}
        endIcon={endIcon}
      >
        {animatedNameSpan}
      </Button>
    );
  }

  return (
    <Button
      fullWidth
      size='medium'
      variant='text'
      disabled={disabled}
      sx={{
        ...commonContainerStyle,
        pl: isIndented ? '56px' : '4px',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'primary.darker',
        },
      }}
      startIcon={startIcon}
      endIcon={endIcon}
    >
      {animatedNameSpan}
    </Button>
  );
}

interface SectionTitleProps {
  title: string;
}

export function SectionTitle({ title }: SectionTitleProps) {
  const { isOpen } = useCustomSidebar();

  if (!isOpen) {
    return (
      <Divider
        sx={{
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          marginX: '16px',
          borderColor: 'neutral.0',
          opacity: 0.3,
          '&::before, &::after': {
            borderColor: 'neutral.0',
          },
        }}
      />
    );
  }

  return (
    <Typography
      variant='body-small'
      sx={{
        color: 'neutral.0',
        justifyContent: 'left',
        height: 32,
        textTransform: 'uppercase',
        paddingLeft: '16px',
        display: 'flex',
        alignItems: 'center',
        opacity: 0.6,
      }}
    >
      {title}
    </Typography>
  );
}

interface CustomSidebarGroupProps {
  name: string;
  Icon: ComponentType<{ sx?: object }>;
  children: ReactNode;
}

export function CustomSidebarGroup({
  name,
  Icon,
  children,
}: CustomSidebarGroupProps) {
  const { isOpen } = useCustomSidebar();
  const [isExpanded, toggleExpanded] = React.useState<boolean>(false);
  const setExpanded = (): void => {
    toggleExpanded(true);
  };
  const handleChange = (_event: SyntheticEvent, expanded: boolean): void => {
    toggleExpanded(expanded);
  };
  return (
    <Accordion
      disableGutters={true}
      expanded={isExpanded}
      onChange={handleChange}
      sx={{
        backgroundColor: 'transparent',
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
        '&:before': { display: 'none' },
      }}
    >
      <AccordionSummary
        sx={{
          padding: 0,
          margin: 0,
          '& .MuiAccordionSummary-content': { display: 'block', margin: 0 },
          justifyContent: 'left',
        }}
      >
        <CustomSidebarButton
          name={name}
          Icon={Icon}
          isInsideAccordionSummary={true}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        {React.Children.map(children, (child) =>
          React.cloneElement(child as React.ReactElement<any>, {
            isIndented: isOpen,
            selectedCallback: setExpanded,
          }),
        )}
      </AccordionDetails>
    </Accordion>
  );
}
